import { useEffect, useState } from "react";
import { request } from "@insite/client-framework/Services/ApiService";
import { getUnitNetPrice, getUnitListPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import translate from "@insite/client-framework/Translate";
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";

export interface ProductLike {
    id?: string;
    productNumber?: string;
    detailName?: string;
    pricing?: ProductPriceDto;
}

export interface UseProductPackSizeResult {
    multipleSaleQty: number | null;
    isMultiple: boolean;
    messageText: string | null;
}

export interface UseProductPackSizeOptions {
    enabled?: boolean;
}

export function useProductPackSize(product?: ProductLike, options?: UseProductPackSizeOptions): UseProductPackSizeResult {
    const [multipleSaleQty, setMultipleSaleQty] = useState<number | null>(null);
    const isEnabled = options?.enabled ?? true;

    // Wait for pricing data before checking if on sale
    const hasPricing = isEnabled && !!product?.pricing;
    const isLiquidation = product?.detailName !== '';
    useEffect(() => {
        // Skip API call entirely if disabled, no pricing data yet, product is on sale, or no product
        if (!isEnabled || !hasPricing || isLiquidation || !product?.id) {
            setMultipleSaleQty(null);
            return;
        }
        
        const url = `/api/v1/SpiPackSize/GetPackSize/${encodeURIComponent(product.id || "")}`;
        let cancelled = false;

        const fetchPackSize = async () => {
            try {
                const resp = await request<number | null>(url, "GET", {
                    "X-Requested-With": "XMLHttpRequest",
                });
                if (!cancelled) {
                    setMultipleSaleQty(resp ?? null);
                }
            } catch {
                if (!cancelled) {
                    setMultipleSaleQty(null);
                }
            }
        };

        fetchPackSize();
        return () => {
            cancelled = true;
        };
    }, [product?.id, isLiquidation, hasPricing, isEnabled]);

    const isMultiple = isEnabled && hasPricing && !isLiquidation && !!multipleSaleQty && multipleSaleQty > 1;
    const messageText = isMultiple
        ? translate("Must be ordered in multiples of {0}", multipleSaleQty.toString())
        : null;
    return { multipleSaleQty: !isEnabled || isLiquidation ? null : multipleSaleQty, isMultiple, messageText };
}
